<template>
  <section class="avg__help">
    <p class="subject">{{ avgData.subject }}</p>

    <div class="formula">
      <div class="formulaHead">
        <span v-if="!avgData.type">{{ t('formulaHead') }}</span>
        <span v-else>{{ t('formulaHeadHalfYear') }}</span>
      </div>
      <div class="formulaBody">
        <img v-if="isPrimarySchool" :src="formulaStartSchoolImage" alt="formula-primary-school" />
        <img v-else-if="!avgData.type" :src="webImage" alt="formula-quarter" />
        <img v-else :src="formulaImage" alt="formula-half-year" />
      </div>
    </div>

    <div class="avgGradeFormula">
      <div class="avgGrade mr-3 ml-3">
        <span class="avgTitle">{{ t('avgGrade') }}:</span>
        <span class="grade">{{ avgData.grade }}</span>
      </div>
      <div class="avgFormulaViewport" tabindex="0">
        <div class="avgFormula" v-html="avgData.formula"></div>
      </div>
      <div class="avgGradeHead">
        <div v-for="item in formulaLegend" :key="item.color" class="avgBody">
          <div class="avgCircule">
            <div class="avgCirculeInner" :class="item.color"></div>
          </div>
          <span>{{ item.name }}</span>
        </div>
      </div>
    </div>

    <div class="d-flex flex-column">
      <span class="maxBall">{{ t('maxBall') }}</span>
      <p class="takedProcent">
        {{ t(isPrimarySchool ? 'takedProcentPrimary' : 'takedProcent') }}:
      </p>
    </div>

    <div class="gradeRating">
      <div class="gradeRatingHead">{{ t('gradeRatingHead') }}</div>
      <div class="gradeRatingBody">
        <div v-for="item in gradeRating" :key="item.grade" class="gradeRatingItem">
          <div class="gradeItem">{{ item.grade }}</div>
          <span class="ratingItem">{{ item.rating }}</span>
        </div>
      </div>
    </div>

    <p class="text">{{ t('order') }}</p>
    <a target="_blank" rel="noopener noreferrer" class="linkYoutube" href="https://youtu.be/8wXmVNOHm7E">
      https://youtu.be/8wXmVNOHm7E
    </a>
  </section>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import formulaImage from '@/assets/formula.svg';
import formulaStartSchoolImage from '@/assets/formulaStartSchool.svg';
import webImage from '@/assets/web.svg';
import { useAvgStore } from '@/stores/avg';
import { useTopbarStore } from '@/stores/topbar';

const { t } = useI18n();
const router = useRouter();
const avgStore = useAvgStore();
const topbarStore = useTopbarStore();

avgStore.initFromStorage();

const avgData = computed(() => avgStore.data || {});
const isPrimarySchool = computed(() => {
  const classNumber = Number(avgData.value?.classNumber || 0);
  return classNumber >= 1 && classNumber <= 4;
});
const gradeRating = [
  { grade: '2', rating: '0 - 39' },
  { grade: '3', rating: '40 - 64' },
  { grade: '4', rating: '65 - 84' },
  { grade: '5', rating: '85 - 100' }
];
const formulaLegend = computed(() => [
  { name: t(isPrimarySchool.value ? 'avgLegendIntermediate' : 'avgLegendExam'), color: 'exam' },
  { name: t('avgLegendFormative'), color: 'gradeColor' }
]);

onMounted(() => {
  if (!avgData.value?.quarter) {
    router.replace('/grades-rating');
    return;
  }

  topbarStore.setTitle(`${t('avg_help')} ${avgData.value.quarter} ${t('fourthShort')}`);
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => {
      router.push('/grades-rating');
    }
  });
});
</script>

<style scoped lang="scss">
.avgGradeHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  background: #efefef;
}

@media screen and (max-width: 768px) {
  .avgGradeHead {
    padding: 8px;
    font-size: 14px;
  }
}

@media screen and (min-width: 768px) {
  .avgGradeHead {
    padding: 12px 32px;
  }
}

.avgBody {
  display: flex;
  align-items: center;
}

.avgCircule {
  border-radius: 50%;
  background: #fff;
  width: 18px;
  height: 18px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exam {
  border-radius: 50%;
  background: #ff5d8a;
  width: 12px;
  height: 12px;
}

.gradeColor {
  border-radius: 50%;
  background: #6696ec;
  width: 12px;
  height: 12px;
}

.avgGradeFormula {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding-top: 24px;
  margin-top: 16px;
}

.avgFormulaViewport {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
  overscroll-behavior-inline: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.avgFormula {
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
}

.maxBall {
  margin-top: 16px;
  color: #eb5757;
}

@media screen and (min-width: 768px) {
  .maxBall {
    font-size: 18px;
    margin-top: 24px;
  }
}

@media screen and (min-width: 768px) {
  .takedProcent {
    font-size: 18px;
    margin-bottom: 24px;
  }
}

@media screen and (min-width: 768px) {
  .linkYoutube {
    font-size: 18px;
  }
}

@media screen and (max-width: 768px) {
  .avgFormulaViewport {
    margin-bottom: 24px;
    padding: 0 12px 4px;
  }

  .avgFormula {
    font-size: clamp(12px, 3.8vw, 14px);
  }
}

@media screen and (min-width: 768px) {
  .avgFormulaViewport {
    margin-bottom: 12px;
    padding-inline: 32px;
  }

  .avgFormula {
    font-size: 22px;
  }
}

@media screen and (min-width: 768px) {
  .text {
    font-size: 18px;
    margin-top: 30px;
    margin-bottom: 24px;
  }
}

.text {
  margin-top: 16px;
}

.gradeRating {
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.gradeRatingItem {
  display: flex;
  align-items: center;
  margin: 8px 0;
}

.gradeRatingBody {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 16px;
}

.gradeRatingHead {
  font-family: 'Open Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: #323c47;
  background: #ccebff;
  border-radius: 4px 4px 0 0;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 16px;
}

@media screen and (min-width: 768px) {
  .gradeRatingHead {
    font-size: 18px;
  }
}

.gradeItem {
  background: #fff;
  border: 1px solid #0199fd;
  font-weight: 700;
  color: #0199fd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

@media screen and (max-width: 768px) {
  .gradeItem {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
}

@media screen and (min-width: 768px) {
  .gradeItem {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
}

.ratingItem {
  font-family: 'Open Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  color: #323c47;
}

@media screen and (min-width: 768px) {
  .ratingItem {
    font-size: 18px;
  }
}

.avgGrade {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}

@media screen and (min-width: 768px) {
  .avgGrade {
    font-size: 18px;
    padding-inline: 32px;
  }
}

.avgTitle {
  color: #323c47;
  font-weight: bold;
}

.grade {
  background: #0199fd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  color: #fff;
  margin-left: 8px;
}

@media screen and (max-width: 768px) {
  .grade {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
}

@media screen and (min-width: 768px) {
  .grade {
    font-size: 18px;
    width: 40px;
    height: 40px;
  }
}

@media screen and (max-width: 768px) {
  .avg__help {
    padding: 16px;
  }
}

@media screen and (min-width: 768px) {
  .avg__help {
    padding: 24px;
  }
}

.subject {
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
}

@media screen and (max-width: 768px) {
  .subject {
    font-size: 18px;
  }
}

@media screen and (min-width: 768px) {
  .subject {
    font-size: 22px;
    margin-bottom: 24px;
  }
}

.formula {
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.formulaHead {
  padding: 6px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #d7f0dd;
  border-radius: 4px 4px 0 0;
}

@media screen and (min-width: 768px) {
  .formulaHead {
    font-size: 18px;
  }
}

.formulaBody {
  padding: 16px;
}

.formulaBody img {
  width: 100%;
}

@media screen and (max-width: 768px) {
  .formulaBody img {
    max-width: 350px;
  }
}

@media screen and (min-width: 768px) {
  .formulaBody img {
    max-width: 520px;
  }
}
</style>
